/** Grid-Datenquelle — Pendant zu `Client/Adaptors/Base/ChinaClientDataAdaptorBase.cs`. */

import type { BaseEntity } from '@/types/entities';

import type { ViewApi } from './apiViewBase';
import type {
  FilterValue,
  GridDataRequest,
  GridDataResult,
  GridPredicate,
  GridSort,
  GridWhere,
} from './gridTypes';

/** Der Filterdialog des Altclients markiert Negationen mit diesem Präfix im Wert. */
const NOT_PREFIX = '$$NOT$$ ';

export interface ViewDataSource<T extends BaseEntity> {
  read(request: GridDataRequest): Promise<GridDataResult<T>>;
  /** Zuletzt an die API gesendeter Where-Ausdruck (Excel-Export). */
  readonly previousWhereQuery: string;
}

/** Baut `"Feld asc,Feld2 desc"` aus den Sortierungen. */
export function buildOrderBy(sorts: GridSort[]): string {
  return sorts
    .map((sort) => `${sort.field} ${sort.direction === 'descending' ? 'desc' : 'asc'}`)
    .join(',');
}

/** Datumswerte als ISO-String, Zahlen ohne Tausendertrennzeichen, `null` als leerer Text. */
function formatValue(value: FilterValue): string {
  if (value === null) {
    return '';
  }
  if (value instanceof Date) {
    return value.toISOString();
  }
  return String(value);
}

function buildPredicate(predicate: GridPredicate): string {
  let operator: string = predicate.operator === 'none' ? 'equal' : predicate.operator;
  let value = formatValue(predicate.value);

  if (typeof predicate.value === 'string' && predicate.value.startsWith(NOT_PREFIX)) {
    value = predicate.value.replaceAll(NOT_PREFIX, '');
    operator = `not${operator}`;
  }

  return `${predicate.field} ${operator} "${value}"`;
}

/** Baut den Where-Ausdruck aus den Grid-Filtern — Pendant zu `GenerateString`. */
export function buildWhereQuery(filters: GridWhere[]): string {
  return filters
    .flatMap((filter) => filter.predicates)
    .map(buildPredicate)
    .join(' && ');
}

export function createViewDataSource<T extends BaseEntity>(
  api: ViewApi<T>,
  getViewId: () => number,
): ViewDataSource<T> {
  // `PreviousQueryString` ist im Altclient anfangs null und damit auch vom leeren
  // Where-Ausdruck verschieden — der erste Aufruf lädt maxcount deshalb immer.
  let previousWhereQuery: string | null = null;
  let previousMaxCount = 0;

  async function read(request: GridDataRequest): Promise<GridDataResult<T>> {
    let pageSize = 0;
    let page = 0;
    if (request.take !== 0) {
      pageSize = request.take;
      page = Math.trunc(request.skip / request.take) + 1;
    }

    const orderBy = buildOrderBy(request.sorted);
    const whereQuery = buildWhereQuery(request.where);

    // maxcount wird nur neu geladen, wenn sich der Where-Ausdruck geändert hat.
    let maxCount: number;
    if (previousWhereQuery !== whereQuery) {
      previousMaxCount = await api.getMaxCount(getViewId(), whereQuery);
      maxCount = previousMaxCount;
      previousWhereQuery = whereQuery;
    } else {
      maxCount = previousMaxCount;
    }

    // Angeforderte Seite liegt hinter dem Ende => auf die letzte Seite zurücksetzen.
    // Der Altclient rechnet `Math.Ceiling` auf einer bereits ganzzahligen Division
    // (long / int), das Aufrunden ist dort also wirkungslos. Für identisches
    // Verhalten wird hier abgerundet.
    if (page * pageSize > maxCount + pageSize) {
      page = Math.floor(maxCount / pageSize);
    }

    const result = await api.get(getViewId(), { pageSize, page, orderBy, whereQuery });
    return { result, count: maxCount };
  }

  return {
    read,
    get previousWhereQuery(): string {
      return previousWhereQuery ?? '';
    },
  };
}
